import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import Form from "react-bootstrap/Form";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_DensityState,
  type MRT_Row,
  MRT_SortingState,
  MRT_VisibilityState,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import { Col, Container, Row } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import {
  CompactTextField,
  CompactSelect,
} from "../../Components/SharedComponents/SharedComponents";
import { lazy } from "react";
import FileUpload from "../../Components/FileUpload/FileUpload";
import BrokerTemplateDownloadButton from "../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import { Clearer, ClearerRate, ClearerRateFilterMode, ClearerRateFormErrors, ClearerRateListResponse, ClearerRatePagination, ClearerRateSorting, ClearerRateTouched, Contract } from "../../Types/ClearerRate.type";
import { bulkDeleteClearerRatesAPI, createClearerRateAPI, deleteClearerRateAPI, exportAllClearerRatesAPI, getClearerRateClearersAPI, getClearerRateContractsAPI, listClearerRatesAPI, uploadClearerRateFileAPI } from "../../api/Clearerrate.actions";
const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((module) => ({
    default: module.ReactQueryDevtools,
  }))
);

export const APPLY_MODE_GENERIC_API = "generic";
export const APPLY_MODE_ONLY_FOR_API = "onlyfor";

export const FIELD_APPLY_MODE = "apply_mode";

// ─── Column Headers ───────────────────────────────────────────────────────────
export const HEADER_CONTRACT = "Contract";
export const HEADER_APPLY_MODE = "Apply Mode";
export const HEADER_CLEARER = "Clearer";
export const HEADER_CLEARER_HOUSE_FEE = "Clearer House Fee";

const cellSx = {
  height: "10px",
  cursor: "pointer",
  "&:hover": { backgroundColor: "rgba(44, 4, 4, 0.05)" },
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

const getEditingCellSx = (isEditing: boolean) => ({
  ...cellSx,
  ...(isEditing && {
    backgroundColor: "rgba(0, 128, 0, 0.1)",
    transition: "background-color 0.3s ease",
  }),
});

const sharedCellProps = (row: any, table: any) => ({
  onDoubleClick: (e: React.MouseEvent) => {
    e.stopPropagation();
    table.setEditingRow(row);
  },
  sx: getEditingCellSx(table.getState().editingRow?.id === row.id),
});


const Example = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const isFirstRender = useRef(true);
  const [filterModes, setFilterModes] = useState<ClearerRateFilterMode[]>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {}
  );
  const [density, setDensity] = useState<MRT_DensityState>("comfortable");
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(
    undefined
  );
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [clearers, setClearers] = useState<Clearer[]>([]);
  const [formData, setFormData] = useState({
    clearer: "",
    contract: "",
    apply_mode: "",
    clearer_house_fees: "",
  });
  const [formErrors, setFormErrors] = useState<ClearerRateFormErrors>({});
  const [touched, setTouched] = useState<ClearerRateTouched>({});
  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);

  const QUERY_KEY = "clearer-rates";
  const queryClient = useQueryClient();

  const handleFileUploadSubmit = async (file: File) => {
    try {
      const result = await uploadClearerRateFileAPI(file);
      if (result instanceof Error) throw result;

      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

      return {
        success: true,
        message: "File uploaded and data added successfully!",
        data: result.data,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || "Failed to upload file",
      };
    }
  };

  const handleBlur = (
    field: keyof typeof formData,
    value: string
  ): string | null => {
    let error: string | null = null;

    switch (field) {
      case "clearer":
        if (!value && formData.apply_mode === "20") {
          error = "Clearer is required when Apply Mode is 'Only For'";
        } else if (value && formData.apply_mode === "10") {
          error = "Clearer should not be selected when Apply Mode is 'Generic'";
        }
        break;
      case "contract":
        if (!value) {
          error = "Contract is required";
        }
        break;
      case "apply_mode":
        if (!value) {
          error = "Apply Mode is required";
        }
        break;
      case "clearer_house_fees":
        if (!value) {
          error = "Clearer House Fee is required";
        } else if (isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
          error = "Clearer House Fee must be a positive number";
        }
        break;
    }

    setFormErrors((prev) => ({ ...prev, [field]: error || undefined }));
    setTouched((prev) => ({ ...prev, [field]: true }));

    return error;
  };

  const handleFocus = (field: keyof typeof formData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const res = await getClearerRateContractsAPI();
        if (res instanceof Error) throw res;
        setContracts(res);
      } catch (err) {
        console.error("Error fetching contracts:", err);
      }
    };

    fetchContracts();
  }, []);

  useEffect(() => {
    const fetchClearer = async () => {
      try {
        const res = await getClearerRateClearersAPI();
        if (res instanceof Error) throw res;
        setClearers(res);
      } catch (err) {
        console.error("Error fetching clearers:", err);
      }
    };
    fetchClearer();
  }, []);

  const saveToLocalStorage = (key: string, value: unknown) => {
    if (isFirstRender.current) return;
    localStorage.setItem(key, JSON.stringify(value));
  };


  useEffect(() => saveToLocalStorage("mrt_columnFilters_table_1", columnFilters), [columnFilters]);
  useEffect(() => saveToLocalStorage("mrt_columnVisibility_table_1", columnVisibility), [columnVisibility]);
  useEffect(() => saveToLocalStorage("mrt_density_table_1", density), [density]);
  useEffect(() => saveToLocalStorage("mrt_globalFilter_table_1", globalFilter ?? ""), [globalFilter]);
  useEffect(() => saveToLocalStorage("mrt_showGlobalFilter_table_1", showGlobalFilter), [showGlobalFilter]);
  useEffect(() => saveToLocalStorage("mrt_showColumnFilters_table_1", showColumnFilters), [showColumnFilters]);
  useEffect(() => saveToLocalStorage("mrt_sorting_table_1", sorting), [sorting]);



  useEffect(() => {
    const columnFilters = localStorage.getItem("mrt_columnFilters_table_1");
    const columnVisibility = localStorage.getItem(
      "mrt_columnVisibility_table_1"
    );
    const density = localStorage.getItem("mrt_density_table_1");
    const globalFilter = localStorage.getItem("mrt_globalFilter_table_1");
    const showGlobalFilter = localStorage.getItem(
      "mrt_showGlobalFilter_table_1"
    );
    const showColumnFilters = localStorage.getItem(
      "mrt_showColumnFilters_table_1"
    );
    const sorting = localStorage.getItem("mrt_sorting_table_1");

    if (columnFilters) setColumnFilters(JSON.parse(columnFilters));
    if (columnVisibility) setColumnVisibility(JSON.parse(columnVisibility));
    if (density) setDensity(JSON.parse(density));
    if (globalFilter) setGlobalFilter(JSON.parse(globalFilter) || undefined);
    if (showGlobalFilter) setShowGlobalFilter(JSON.parse(showGlobalFilter));
    if (showColumnFilters) setShowColumnFilters(JSON.parse(showColumnFilters));
    if (sorting) setSorting(JSON.parse(sorting));
    isFirstRender.current = false;
  }, []);

  const handleFormChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "apply_mode" && value === "10" ? { clearer: "" } : {}),
    }));
  };
  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "contract_name",
        header: HEADER_CONTRACT,
        muiTableBodyCellProps: ({ row, table }) => sharedCellProps(row, table),
        muiEditTextFieldProps: ({ row, table }) => ({
          required: true,
          select: true,
          value: row.getValue("contract_name") ?? "",
          error: !!validationErrors?.contract_name,
          helperText: validationErrors?.contract_name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              contract_name: undefined,
            }),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            const selectedContract = contracts.find(
              (contract) => contract.contract_name === newValue
            );
            table.setEditingRow({
              ...row.original,
              contract_name: newValue,
              contract_id: selectedContract?.id,
            });
          },
          children: contracts.map((contract) => (
            <MenuItem key={contract.id} value={contract.contract_name}>
              {contract.contract_name}
            </MenuItem>
          )),
        }),
      },
      {
        accessorKey: FIELD_APPLY_MODE,
        header: HEADER_APPLY_MODE,
        muiTableBodyCellProps: ({ row, table }) => sharedCellProps(row, table),
        muiEditTextFieldProps: ({ row, table }) => {
          const currentApplyMode = row.getValue(FIELD_APPLY_MODE) as string;
          const availableOptions =
            currentApplyMode === APPLY_MODE_ONLY_FOR_API
              ? [
                  <MenuItem key={APPLY_MODE_ONLY_FOR_API} value={APPLY_MODE_ONLY_FOR_API}>
                    Only For
                  </MenuItem>,
                ]
              : [
                  <MenuItem key={APPLY_MODE_GENERIC_API} value={APPLY_MODE_GENERIC_API}>
                    Generic
                  </MenuItem>,
                  <MenuItem key={APPLY_MODE_ONLY_FOR_API} value={APPLY_MODE_ONLY_FOR_API}>
                    Only For
                  </MenuItem>,
                ];

          return {
            required: true,
            select: true,
            value: currentApplyMode ?? "",
            error: !!validationErrors?.apply_mode,
            helperText: validationErrors?.apply_mode,
            onFocus: () =>
              setValidationErrors((prev) => ({ ...prev, apply_mode: undefined })),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const newValue = e.target.value;
              table.setEditingRow({
                ...row.original,
                apply_mode: newValue,
                clearer_name: newValue === APPLY_MODE_GENERIC_API ? "" : row.original.clearer_name,
              });
            },
            children: availableOptions,
          };
        },
      },
      {
        accessorKey: "clearer_name",
        header: "Clearer",
        muiTableBodyCellProps: ({ row, table }) => sharedCellProps(row, table),
        muiEditTextFieldProps: ({ row, table }) => {
          const isGeneric = row.getValue(FIELD_APPLY_MODE) === APPLY_MODE_GENERIC_API;
          return {
            required: !isGeneric,
            select: true,
            disabled: isGeneric,
            value: row.getValue("clearer_name") ?? "",
            error: !!validationErrors?.clearer_name,
            helperText: validationErrors?.clearer_name,
            onFocus: () =>
              setValidationErrors((prev) => ({ ...prev, clearer_name: undefined })),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const newValue = e.target.value;
              const selectedClearer = clearers.find((c) => c.name === newValue);
              table.setEditingRow({
                ...row.original,
                clearer_name: newValue,
                clearer_id: selectedClearer?.id,
              });
            },
            children: clearers.map((clearer) => (
              <MenuItem key={clearer.id} value={clearer.name}>
                {clearer.name}
              </MenuItem>
            )),
          };
        },
        Cell: ({ row }) => row.getValue("clearer_name"),
      },
      {
        accessorKey: "clearer_house_fees",
        header: "Clearer House Fee",
        muiTableBodyCellProps: ({ row, table }) => sharedCellProps(row, table),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.clearer_house_fees,
          helperText: validationErrors?.clearer_house_fees,
          onFocus: () =>
            setValidationErrors({ ...validationErrors, clearer_house_fees: undefined }),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            console.log("Editing clearer_house_fees:", e.target.value);
          },
        },
      },
    ],
    [validationErrors, clearers, contracts]
  );


  const {
    data,
    isError: isLoadingUsersError,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useGetData(filterModes, pagination, setTotalCount, sorting);

  const updateUser = async (user: any) => {
    console.group("🔄 Clearer Rate Update");
    console.log("Input Object:", user);

    if (!user || !user.id) {
      console.error("❌ Invalid object:", user);
      throw new Error("Object is invalid or missing ID");
    }

    const newValue = user.contract_input;
    const selectedContract = contracts.find(
      (contract) => contract.contract_name === newValue
    );

    const newClearer = user.clearer_name;
    const selectedClearer = clearers.find(
      (clearer) => clearer.name === newClearer
    );


    const applyModeMapping: { [key: string]: string } = {
      generic: APPLY_MODE_GENERIC_API,
      onlyfor: APPLY_MODE_ONLY_FOR_API,
      "10": APPLY_MODE_GENERIC_API,
      "20": APPLY_MODE_ONLY_FOR_API,
    };
    const mappedApplyMode =
      applyModeMapping[user.apply_mode] || user.apply_mode;

    const payload = {
      contract_input: selectedContract?.id,
      apply_mode: mappedApplyMode,
      clearer_house_fees: user.clearer_house_fees,
      ...(mappedApplyMode === APPLY_MODE_GENERIC_API
        ? {}
        : { clearer: selectedClearer?.id }),
    };

    try {
      const { data } = await axiosInstance.put(
        `${backendbaseUrl}/paper-app/clearer-rates/${user.id}/`,
        payload
      );

      queryClient.setQueryData([QUERY_KEY], (oldData: any) => {
        if (!oldData?.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((prevItem: any) =>
            prevItem.id === user.id
              ? {
                ...prevItem,
                contract_name: user.contract_input,
                apply_mode: user.apply_mode,
                clearer_house_fees: user.clearer_house_fees,
                clearer_name: user.clearer_name,
              }
              : prevItem
          ),
        };
      });

      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      console.groupEnd();
      return data;
    } catch (error) {
      console.error("❌ API Request Failed:", error);
      toast.error("Failed to update clearer rate. Please try again.");
      throw error;
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      const result = await deleteClearerRateAPI(userId);
      if (result instanceof Error) throw result;

      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  };

  const createBroker = async (broker: ClearerRate) => {
    try {
      const result = await createClearerRateAPI(broker);
      if (result instanceof Error) throw result;

      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    } catch (error: any) {
      console.error("Error adding clearer rates:", error);
      let errorMessage = "Failed to add clearer rate. Please try again.";

      if (error.response?.data?.detail?.string) {
        errorMessage = error.response.data.detail.string;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage =
          typeof error.response.data.detail === "string"
            ? error.response.data.detail
            : errorMessage;
      }

      throw new Error(errorMessage);
    }
  };

  const formCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      console.log("formCreate called");

      const errors: { [key: string]: string } = {};

      // Validation logic
      if (!formData.apply_mode) {
        errors.apply_mode = HEADER_APPLY_MODE;
      }

      if (!formData.contract) {
        errors.contract = " Contract";
      }

      if (!formData.clearer_house_fees) {
        errors.clearer_house_fees = " Clearer House Fees";
      } else if (
        isNaN(parseFloat(formData.clearer_house_fees)) ||
        parseFloat(formData.clearer_house_fees) <= 0
      ) {
        errors.clearer_house_fees =
          "Clearer House Fee must be a positive number";
      }

      // Add validation for clearer field
      if (formData.apply_mode === "20" && !formData.clearer) {
        errors.clearer =
          "Please select a Clearer when Apply Mode is 'Only For'";
      } else if (!formData.apply_mode) {
        errors.clearer = "Please select a Clearer after choosing Apply Mode";
      }

      setFormErrors(errors);
      setTouched({
        clearer: true,
        contract: true,
        apply_mode: true,
        clearer_house_fees: true,
      });

      // If there are errors, combine them into a single toast message
      if (Object.keys(errors).length > 0) {
        const errorMessages = Object.values(errors).join(", ");
        toast.error(`Please required the following: ${errorMessages}`);
        return;
      }

      // Create new clearer rate object
      const isGeneric = formData.apply_mode === "10";
      const newClearerRate: ClearerRate = {
        contract_input: formData.contract,
        apply_mode: isGeneric ? APPLY_MODE_GENERIC_API : APPLY_MODE_ONLY_FOR_API,
        clearer_house_fees: parseFloat(formData.clearer_house_fees),
        ...(isGeneric ? {} : { clearer: formData.clearer }),
      };

      // Submit the form data
      await createBroker(newClearerRate);

      // Reset form on success
      setFormData({
        clearer: "",
        contract: "",
        apply_mode: "",
        clearer_house_fees: "",
      });
      setFormErrors({});
      setTouched({});

      toast.success("Clearer rate added successfully!");

      // Handle window communication if needed
      if (window.opener) {
        window.opener.postMessage({ newClearer: newClearerRate }, "*");
        window.close();
      }
    } catch (error: any) {
      console.error("Error in formCreate:", error);

      // Extract meaningful error message
      let errorMessage = "Failed to add Clearer rate. Please try again.";

      if (error.response?.data?.error) {
        const errorString = error.response.data.error;

        // Extract message from string format like "{'non_field_errors': [ErrorDetail(string='...', code='invalid')]}"
        const match = errorString.match(/string='([^']+)'/);
        if (match && match[1]) {
          errorMessage = match[1];
        } else {
          errorMessage = errorString;
        }
      } else if (error.response?.data?.non_field_errors?.length > 0) {
        // Handle proper JSON structure (your original code)
        const firstError = error.response.data.non_field_errors[0];
        if (firstError.string) {
          errorMessage = firstError.string;
        }
      } else if (error.response?.data?.detail?.string) {
        errorMessage = error.response.data.detail.string;
      } else if (error.response?.data?.detail) {
        errorMessage =
          typeof error.response.data.detail === "string"
            ? error.response.data.detail
            : errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      // Reset form fields regardless of success or error
      setFormData({
        clearer: "",
        contract: "",
        apply_mode: "",
        clearer_house_fees: "",
      });
      setFormErrors({});
      setTouched({});
    }
  };
  const handleCreateUser = async ({
    values,
    table,
  }: {
    values: any;
    table: any;
  }) => {
    try {

      if (!values.contract_name || values.contract_name.trim() === "") {
        setValidationErrors({ contract_name: "Contract is Required" });
        toast.error("Contract is Required");
        return;
      }
      setValidationErrors({});

      const isGeneric = values.apply_mode === "10";
      const newClearerRate: ClearerRate = {
        contract_input: values.contract,
        apply_mode: isGeneric ? APPLY_MODE_GENERIC_API : APPLY_MODE_ONLY_FOR_API,
        clearer_house_fees: parseFloat(values.clearer_house_fees),
        ...(isGeneric ? {} : { clearer: values.clearer }),
      };

      await createBroker(newClearerRate);
      table.setCreatingRow(null);

      // Optional: Add success notification
      toast.success("User created successfully!");
    } catch (error: any) {
      console.error("Error in handleCreateUser:", error);

      // Extract meaningful error message

      if (
        error.response?.status === 400 &&
        error.response?.data?.detail?.includes("already exists")
      ) {
        toast.error("A Clearer Rate with this company name already exists!");
      } else {
        toast.error("Failed to add Clearer Rate. Please try again.");
      }

    }
  };

  const handleEditSave = async ({
    values,
    table,
    row,
  }: {
    values: any;
    table: any;
    row: MRT_Row<any>;
  }) => {
    console.log(
      "handleEditSave called for row:",
      row.id,
      "with values:",
      values
    );
    if (!row.id) {
      console.error("No row ID provided, aborting update");
      toast.error("Invalid row data. Update aborted.");
      return;
    }

    const isGeneric = values.apply_mode === APPLY_MODE_GENERIC_API;
    const updatedUser = {
      ...row.original,
      id: row.id,
      contract_input: values.contract_name,
      apply_mode: values.apply_mode,
      clearer_house_fees: parseFloat(values.clearer_house_fees),
      clearer_name: isGeneric ? undefined : values.clearer_name,
    };

    if (!updatedUser.contract_name || updatedUser.contract_name.trim() === "") {
      setValidationErrors({ contract_name: "Contract is Required" });
      toast.error("Contract is Required");
      return;
    }
    setValidationErrors({});
    try {
      await updateUser(updatedUser);
      table.setEditingRow(null);
      toast.success("Clearer rate updated successfully!");
    } catch (error) {
      console.error("User Update Error:", error);
      toast.error("Failed to update clearer rate. Please try again.");
    }
  };

  const openDeleteConfirmModal = (row: MRT_Row<any>) => {
    console.log("openDeleteConfirmModal called for row:", row.id);
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this clearer rate?",
      content: "",
      onOk: () => {
        deleteUser(row.original.id)
          .then(() => {
            toast.success("Clearer rate deleted successfully!");
          })
          .catch((error) => {
            toast.error("Failed to delete clearer rate. Please try again.");
          });
      },
    });
  };

  const handleCopyRow = (row: MRT_Row<any>) => {
    console.log("handleCopyRow called for row:", row.id);

    try {
      if (!row || !row.original) {
        toast.error("No Clearer rate row data to copy");
        return;
      }

      const { contract_name, apply_mode, clearer_name, clearer_house_fees } =
        row.original;

      const matchedContract = contracts.find(
        (c) => c.contract_name === contract_name
      );
      const matchedClearer = clearers.find((c) => c.name === clearer_name);

      if (!matchedContract) {
        toast.error(`Contract "${contract_name}" not found in options`);
        return;
      }
      if (!matchedClearer && apply_mode === APPLY_MODE_ONLY_FOR_API) {
        toast.error(`Clearer "${clearer_name}" not found in options`);
        return;
      }

      const mappedApplyMode =
        apply_mode === APPLY_MODE_GENERIC_API
          ? "10"
          : apply_mode === APPLY_MODE_ONLY_FOR_API
            ? "20"
            : "10";

      setFormData({
        contract: matchedContract.id,
        apply_mode: mappedApplyMode,
        clearer: apply_mode === APPLY_MODE_GENERIC_API ? "" : matchedClearer?.id || "",
        clearer_house_fees: String(clearer_house_fees),
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => setInputBackgroundColour(""), 2400);
      toast.success("Clearer rate Row data copied to form");
    } catch (error: any) {
      console.error("Failed to copy and add clearer rate:", error);
      toast.error("An error occurred while copying row data");
    }
  };

  interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
  }

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(
    null
  );

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleResizing = () => {
    setShowRowResizing(!showRowResizing);
    handleClose();
  };

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const rowData = rows.map((row) => row.original);
    console.log("Exporting rows:", rowData);
  };

  const handleDataDownload = async () => {
    try {
      const blobResult = await exportAllClearerRatesAPI();
      if (blobResult instanceof Error) throw blobResult;
      const url = window.URL.createObjectURL(blobResult);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clearer_rates.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const handleBulkDelete = async (rows: MRT_Row<any>[]) => {
    const rowIds = rows.map((row) => row.original.id);

    if (rowIds.length === 0) {
      toast.warn("No rows selected for deletion.");
      return;
    }

    showDeleteConfirmModal({
      title: `Delete ${rowIds.length} selected clearer rate(s)?`,
      content: `You are about to delete ${rowIds.length} clearer rate(s). This action cannot be undone.`,
      onOk: async () => {
        try {
          const loadingToast = toast.loading(`Deleting ${rowIds.length} clearer rate(s)...`);

          const result = await bulkDeleteClearerRatesAPI(rowIds);
          if (result instanceof Error) throw result;

          toast.success(`${rowIds.length} clearer rate(s) deleted successfully!`);
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
          table.setRowSelection({});

          toast.dismiss(loadingToast);
        } catch (error: any) {
          toast.error("Failed to delete clearer rates");
          toast.dismiss();
        }
      },
    });
  };


  const table = useMaterialReactTable({
    columns,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onSortingChange: setSorting,
    manualSorting: true,
    manualPagination: true,
    enableRowSelection: true,
    // positionToolbarAlertBanner: "bottom",
    // enableColumnResizing: showRowResizing,
    enableColumnResizing: true, // Already set
    // columnResizeMode: "onEnd", // Ensures precise resizing
    // defaultColumn: {
    //   minSize: 50, // Smallest practical width
    //   enableResizing: true,
    // },
    enableFilterMatchHighlighting: true,
    rowCount: totalCount,
    manualFiltering: true,
    data: data?.results || [],
    enableDensityToggle: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,

    // Top toolbar customization
    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),

    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),

    muiTableBodyRowProps: ({ row, table }) => ({
      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          const editingRow = table.getState().editingRow;
          if (editingRow) {
            console.log("Enter key pressed, saving row:", editingRow.id);
            const saveHandler = table.options.onEditingRowSave;
            if (saveHandler) {
              const newedit = {
                ...editingRow.original,
                ...editingRow._valuesCache,
              };
              saveHandler({
                exitEditingMode: () => table.setEditingRow(null),
                row: editingRow,
                table,
                values: newedit,
              });
            }
          }
        }
      },
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
      },
    }),
    enableColumnOrdering: true,
    enableRowOrdering: false,
    getRowId: (row) => row.id,
    enableColumnFilterModes: true,
    // initialState: { showColumnFilters: true },
    filterFns: {
      customFilterFn: (row, id, filterValue) =>
        row.getValue(id) === filterValue,
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 120,
        muiTableHeadCellProps: {
        },
      },
      "mrt-row-numbers": {
        enableColumnDragging: true,
        enableColumnOrdering: true,
        muiTableHeadCellProps: {
          sx: {
            fontSize: "1.2rem",
          },
        },
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        className="table-download__wrapper"
        sx={{
          display: "flex",
          gap: "6px",
          padding: "0px",
          flexWrap: "wrap",
        }}
      >
        <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
          Upload File
        </Button>
        <Button
          onClick={() => handleDataDownload()}
          startIcon={<BrowserUpdatedIcon />}
        >
          Export All Data
        </Button>
        <BrokerTemplateDownloadButton
          apiEndpoint="/paper-app/download-clearer-rate-template/"
          buttonText="Download Template"
          fileName="clearer_rate_template.csv"
        />
        <Button
          disabled={
            !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
          }
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows
        </Button>
        {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
          <Button
            style={{ color: "red" }}
            onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "normal",
        fontWeight: "normal",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        border: "0",
      },
    },
    localization: {
      filterCustomFilterFn: "Custom Filter Fn",
    } as any,
    muiToolbarAlertBannerProps: isLoadingUsersError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateUser,
    onEditingRowCancel: () => {
      console.log("Editing cancelled");
      setValidationErrors({});
    },
    onEditingRowSave: handleEditSave,
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip title="Edit" className="editIcon">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              console.log("Edit button clicked for row:", row.id);
              table.setEditingRow(row);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteConfirmModal(row);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleCopyRow(row);
            }}
          >
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    state: {
      sorting,
      columnFilters,
      pagination,
      isLoading: isLoadingUsers,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
      showColumnFilters: true,
    },
  });

  useEffect(() => {
    const transformedFilters = columnFilters.map((filter) => ({
      ...filter,
      mode:"contains",
    }));
    setFilterModes(transformedFilters);
  }, [columnFilters, table]);

  // Debug table editing state changes
  useEffect(() => {
    console.log("Table editingRow:", table.getState().editingRow?.id);
  }, [table.getState().editingRow]);

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Add New Clearer Rates
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form onSubmit={formCreate}>
                      <div className="form_contain">
                        <div
                          className={`flex formWrapper ${inputBackgroundColour !== "" ? "inputboxcustom" : ""
                            }`}
                          style={{ backgroundColor: inputBackgroundColour }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              p: { xs: 1, sm: 1 },
                              pt: "20px !important",
                              backgroundColor: "#ffff",
                              borderRadius: "4px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              className="form_default"
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                                justifyContent: "start",
                                gap: 0.5,
                                "& > *": {
                                  minWidth: {
                                    xs: "100%",
                                    sm: "48%",
                                    md: "23%",
                                    lg: "15%",
                                    xl: "7.5%",
                                  },
                                },
                              }}
                            >
                              <CompactSelect
                                label={HEADER_CONTRACT}
                                value={formData.contract}
                                onChange={(value) =>
                                  handleFormChange("contract", value)
                                }
                                name="contract"
                                options={contracts.map((contract) => ({
                                  value: contract.id,
                                  label: contract.contract_name,
                                }))}
                                error={Boolean(
                                  formErrors.contract && touched.contract
                                )}
                                validateOnBlur={(value) =>
                                  handleBlur("contract", value)
                                }
                                onFocus={() => handleFocus("contract")}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                              <CompactSelect
                                label={HEADER_APPLY_MODE}
                                value={formData.apply_mode}
                                onChange={(value) =>
                                  handleFormChange(FIELD_APPLY_MODE, value)
                                }
                                name={FIELD_APPLY_MODE}
                                options={[
                                  { value: "10", label: APPLY_MODE_GENERIC_API},
                                  { value: "20", label: APPLY_MODE_ONLY_FOR_API },
                                ]}
                                error={Boolean(
                                  formErrors.apply_mode && touched.apply_mode
                                )}
                                validateOnBlur={(value) =>
                                  handleBlur(FIELD_APPLY_MODE, value)
                                }
                                onFocus={() => handleFocus(FIELD_APPLY_MODE)}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                              <CompactSelect
                                label="Clearer"
                                value={formData.clearer}
                                onChange={(value) =>
                                  handleFormChange("clearer", value)
                                }
                                name="clearer"
                                disabled={formData.apply_mode === "10"}
                                options={clearers.map((clearer) => ({
                                  value: clearer.id,
                                  label: clearer.name,
                                }))}
                                error={Boolean(
                                  formErrors.clearer && touched.clearer
                                )}
                                validateOnBlur={(value) =>
                                  handleBlur("clearer", value)
                                }
                                onFocus={() => handleFocus("clearer")}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                              <CompactTextField
                                label="Clearer House Fee"
                                value={formData.clearer_house_fees}
                                onChange={(value) =>
                                  handleFormChange("clearer_house_fees", value)
                                }
                                name="clearer_house_fees"
                                error={Boolean(
                                  formErrors.clearer_house_fees &&
                                  touched.clearer_house_fees
                                )}
                                validateOnBlur={(value) =>
                                  handleBlur("clearer_house_fees", value)
                                }
                                onFocus={() =>
                                  handleFocus("clearer_house_fees")
                                }
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                              />
                              <Button
                                variant="contained"
                                fullWidth
                                type="submit"
                                sx={{
                                  height: { xs: "36px", sm: "32px" },
                                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                  minWidth: { xs: "100%", sm: "100px" },
                                  maxWidth: { sm: "150px" },
                                  backgroundColor: "#00bcd4",
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  "&:hover": {
                                    backgroundColor: "#008c9e",
                                  },
                                }}
                              >
                                Add
                              </Button>
                            </Box>
                          </Box>
                        </div>
                      </div>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              <div
                className="table-ad__wrapper"
                onContextMenu={handleContextMenu}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />

                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                    }
                  >
                    <MenuItem onClick={handleResizing}>
                      {showRowResizing ? "Disable Resizing" : "Enable Resizing"}
                    </MenuItem>
                  </Menu>
                </div>
                <FileUpload
                  open={openFileUpload}
                  onFileUploadClose={handleFileUploadClose}
                  onFileUploadSubmit={handleFileUploadSubmit}
                  acceptedFileTypes=".csv"
                />
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

// READ hook (get users from api)
function useGetData(
  filterModes: ClearerRateFilterMode[],
  pagination: ClearerRatePagination,
  setTotalCount: (count: number) => void,
  sorting: ClearerRateSorting[]
) {
  return useQuery<ClearerRateListResponse>({
    queryKey: ["clearer-rates", filterModes, pagination, sorting],
    queryFn: async () => {
      const result = await listClearerRatesAPI(filterModes, pagination, sorting);
      if (result instanceof Error) throw result;
      setTotalCount(result.count);
      return result;
    },
    refetchOnWindowFocus: false,
  });
}

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
      <Suspense fallback={null}>
        <ReactQueryDevtools initialIsOpen={false} />
      </Suspense>
    </QueryClientProvider>
  );
}

